import React, { useState, useEffect, useCallback } from 'react';
import {
  staffAvailabilityApi,
  type StaffReservation,
  type PendingDeclaration,
  type WeekDeclaration,
  type ReservationAssignment,
} from '../lib/api';
import {
  CreateReservationModal,
  EditReservationModal,
  CancelReservationModal,
  RejectDeclarationModal,
  ApproveDeclarationModal,
} from './AvailabilityReservationModals';
import { AssignProviderModal } from './AssignProviderModal';
import { SpotActionsModal, DeclarationActionsModal } from './SpotActionsModal';
import { AvailabilityWeekGrid, isoDate } from './AvailabilityWeekGrid';

/**
 * Staff Availability admin — roster management + approval queue.
 *
 * Top: Shifts panel — week nav, list shifts, Create/Edit/Cancel modals.
 * Bottom: Pending approvals — Approve (inline confirm) or Reject (modal).
 */
export function AvailabilityScreen() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [shifts, setReservations] = useState<StaffReservation[]>([]);
  const [pending, setPending] = useState<PendingDeclaration[]>([]);
  const [loadingReservations, setLoadingShifts] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [declarations, setDeclarations] = useState<WeekDeclaration[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editReservation, setEditReservation] = useState<StaffReservation | null>(null);
  const [cancelReservation, setCancelReservation] = useState<StaffReservation | null>(null);
  const [assignReservation, setAssignReservation] = useState<StaffReservation | null>(null);
  const [spotActions, setSpotActions] = useState<
    { shift: StaffReservation; assignment: ReservationAssignment } | null
  >(null);
  const [declActions, setDeclActions] = useState<WeekDeclaration | null>(null);
  const [rejectDecl, setRejectDecl] = useState<PendingDeclaration | null>(null);
  const [approveDecl, setApproveDecl] = useState<PendingDeclaration | null>(null);

  const weekStart = computeMonday(weekOffset);
  const weekParam = isoDate(weekStart);
  const weekLabel = formatWeekRange(weekStart);

  // Slots and off-roster declarations (custom hours, on call) are separate
  // endpoints but one view — fetch them together so the grid never renders
  // half a week.
  const refreshReservations = useCallback(async () => {
    setLoadingShifts(true);
    try {
      const [shiftRes, declRes] = await Promise.all([
        staffAvailabilityApi.listReservations(weekParam),
        staffAvailabilityApi.listWeekDeclarations(weekParam),
      ]);
      setReservations(shiftRes.reservations);
      setDeclarations(declRes.declarations);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingShifts(false);
    }
  }, [weekParam]);

  const refreshPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await staffAvailabilityApi.listPending();
      setPending(res.pending);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingPending(false);
    }
  }, []);

  useEffect(() => { refreshReservations(); }, [refreshReservations]);
  useEffect(() => { refreshPending(); }, [refreshPending]);

  return (
    <div className="avail-screen">
      <div className="avail-header">
        <h2 className="avail-title">Availability</h2>
        <div className="avail-week-nav">
          <button
            type="button"
            className="avail-nav-button"
            onClick={() => setWeekOffset(w => w - 1)}
            aria-label="Previous week"
          >‹</button>
          <span className="avail-week-label">{weekLabel}</span>
          <button
            type="button"
            className="avail-nav-button"
            onClick={() => setWeekOffset(w => w + 1)}
            aria-label="Next week"
          >›</button>
          {weekOffset !== 0 && (
            <button
              type="button"
              className="avail-today-button"
              onClick={() => setWeekOffset(0)}
            >This week</button>
          )}
        </div>
      </div>

      {error && <div className="modal-error">{error}</div>}

      <div className="avail-panels">

        <section className="avail-panel">
          <div className="avail-panel-header">
            <h3>Shifts</h3>
            <button
              type="button"
              className="avail-add-button"
              onClick={() => setCreateOpen(true)}
            >+ Add reservation</button>
          </div>
          {loadingReservations ? (
            <div className="avail-empty">Loading…</div>
          ) : (
            <>
              {shifts.length === 0 && (
                <div className="avail-empty">
                  No shifts published for this week yet.
                </div>
              )}
              <AvailabilityWeekGrid
                weekStart={weekStart}
                shifts={shifts}
                declarations={declarations}
                onEditReservation={setEditReservation}
                onAssign={setAssignReservation}
                onSpotActions={(shift, assignment) => setSpotActions({ shift, assignment })}
                onDeclarationActions={setDeclActions}
              />
            </>
          )}
        </section>

        <section className="avail-panel">
          <div className="avail-panel-header">
            <h3>
              Pending approvals
              {pending.length > 0 && (
                <span className="avail-badge">{pending.length}</span>
              )}
            </h3>
          </div>
          {loadingPending ? (
            <div className="avail-empty">Loading…</div>
          ) : pending.length === 0 ? (
            <div className="avail-empty">No pending approvals.</div>
          ) : (
            <table className="avail-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Service Provider</th>
                  <th>Request</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pending.map(p => (
                  <PendingRow
                    key={p.id}
                    item={p}
                    onApproved={() => { refreshPending(); refreshReservations(); }}
                    onApproveWithSection={() => setApproveDecl(p)}
                    onReject={() => setRejectDecl(p)}
                    onError={setError}
                  />
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {createOpen && (
        <CreateReservationModal
          defaultDate={isoDate(weekStart)}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); refreshReservations(); }}
        />
      )}

      {editReservation && (
        <EditReservationModal
          shift={editReservation}
          onClose={() => setEditReservation(null)}
          onChanged={() => { setEditReservation(null); refreshReservations(); }}
          onRequestCancel={() => { setCancelReservation(editReservation); setEditReservation(null); }}
        />
      )}

      {cancelReservation && (
        <CancelReservationModal
          shift={cancelReservation}
          onClose={() => setCancelReservation(null)}
          onChanged={() => { setCancelReservation(null); refreshReservations(); refreshPending(); }}
        />
      )}

      {assignReservation && (
        <AssignProviderModal
          shift={assignReservation}
          onClose={() => setAssignReservation(null)}
          onAssigned={() => { setAssignReservation(null); refreshReservations(); refreshPending(); }}
          onEditReservation={() => { setEditReservation(assignReservation); setAssignReservation(null); }}
        />
      )}

      {spotActions && (
        <SpotActionsModal
          shift={spotActions.shift}
          assignment={spotActions.assignment}
          onClose={() => setSpotActions(null)}
          onChanged={() => { setSpotActions(null); refreshReservations(); }}
          onEditReservation={() => { setEditReservation(spotActions.shift); setSpotActions(null); }}
        />
      )}

      {declActions && (
        <DeclarationActionsModal
          declaration={declActions}
          onClose={() => setDeclActions(null)}
          onChanged={() => { setDeclActions(null); refreshReservations(); }}
        />
      )}

      {approveDecl && (
        <ApproveDeclarationModal
          declarationId={approveDecl.id}
          workingName={approveDecl.profile.workingName}
          dateLabel={formatDate(approveDecl.date)}
          hoursLabel={
            approveDecl.customStart && approveDecl.customEnd
              ? formatTimeRange(approveDecl.customStart, approveDecl.customEnd)
              : 'custom hours'
          }
          onClose={() => setApproveDecl(null)}
          onApproved={() => { setApproveDecl(null); refreshPending(); refreshReservations(); }}
        />
      )}

      {rejectDecl && (
        <RejectDeclarationModal
          declarationId={rejectDecl.id}
          workingName={rejectDecl.profile.workingName}
          dateLabel={formatDate(rejectDecl.date)}
          onClose={() => setRejectDecl(null)}
          onRejected={() => { setRejectDecl(null); refreshPending(); refreshReservations(); }}
        />
      )}
    </div>
  );
}

// --- Pending row ------------------------------------------------------------

interface PendingRowProps {
  item: PendingDeclaration;
  onApproved: () => void;
  onApproveWithSection: () => void;
  onReject: () => void;
  onError: (msg: string | null) => void;
}

function PendingRow({ item, onApproved, onApproveWithSection, onReject, onError }: PendingRowProps) {
  const [busy, setBusy] = useState(false);
  const requestDesc = describeRequest(item);

  // Custom hours need a section chosen before they can be approved, so they
  // go via a modal. Everything else has nothing to ask about and approves
  // inline as before.
  const needsSection = item.mode === 'CUSTOM';

  async function handleApprove() {
    if (busy) return;
    if (needsSection) { onApproveWithSection(); return; }
    setBusy(true);
    onError(null);
    try {
      await staffAvailabilityApi.approveDeclaration(item.id);
      onApproved();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>{formatDate(item.date)}</td>
      <td>
        <strong>{item.profile.workingName}</strong>
        {item.profile.phone && <div className="avail-contact">{item.profile.phone}</div>}
      </td>
      <td>{requestDesc}</td>
      <td className="avail-submitted">{formatRelative(item.createdAt)}</td>
      <td className="avail-actions">
        <button
          type="button"
          className="avail-button-approve"
          onClick={handleApprove}
          disabled={busy}
        >Approve</button>
        {' '}
        <button
          type="button"
          className="avail-button-reject"
          onClick={onReject}
          disabled={busy}
        >Reject</button>
      </td>
    </tr>
  );
}

// --- Helpers ----------------------------------------------------------------

function describeRequest(p: PendingDeclaration): string {
  if (p.mode === 'CUSTOM' && p.customStart && p.customEnd) {
    return `Custom hours: ${formatTimeRange(p.customStart, p.customEnd)}`;
  }
  if (p.mode === 'RESERVED' && p.reservation) {
    const range = formatTimeRange(p.reservation.startTime, p.reservation.endTime);
    return `Reservation: ${range}`;
  }
  // PENDING_OVERFLOW now means she declared herself available and no spot has
  // been found for her yet, so there is no slot to describe.
  if (p.status === 'PENDING_OVERFLOW') return 'Available — no spot assigned yet';
  return p.mode;
}

function isOvernight(start: string, end: string): boolean {
  return end <= start && end !== start;
}

function formatTimeRange(start: string, end: string): string {
  const base = `${start} – ${end}`;
  return isOvernight(start, end) ? `${base} next day` : base;
}

function computeMonday(weekOffset: number): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = today.getDay();
  const mondayShift = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayShift + weekOffset * 7);
  return monday;
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatWeekRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const monthFmt: Intl.DateTimeFormatOptions = { month: 'short' };
  if (sameMonth) {
    return `${monday.getDate()}–${sunday.getDate()} ${sunday.toLocaleDateString('en-AU', monthFmt)} ${sunday.getFullYear()}`;
  }
  return `${monday.getDate()} ${monday.toLocaleDateString('en-AU', monthFmt)} – ${sunday.getDate()} ${sunday.toLocaleDateString('en-AU', monthFmt)} ${sunday.getFullYear()}`;
}

function formatRelative(iso: string): string {
  const created = new Date(iso);
  const now = new Date();
  const mins = Math.floor((now.getTime() - created.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
